import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Divider,
  Button,
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import EventBusyIcon from "@mui/icons-material/EventBusy";

import { useApi } from "../../hooks/useApi";
import { useAuth } from "../../context/AuthContext";
import { useDashContext } from "../../context/DashboardContext";
import { useTenant } from "../../hooks/useTenant";

dayjs.extend(utc);
dayjs.extend(timezone);

// ── Constants ─────────────────────────────────────────────────────────────────
const GOLD = "#9c6a36";
const fmt = (n) =>
  Number(n).toLocaleString("en-US", { style: "currency", currency: "USD" });

const STATUS_STYLES = {
  pending: { bg: "#FAEEDA", text: "#854F0B", label: "Pending" },
  confirmed: { bg: "#E6F1FB", text: "#0C447C", label: "Confirmed" },
  completed: { bg: "#E1F5EE", text: "#0F6E56", label: "Completed" },
  cancelled: { bg: "#FCEBEB", text: "#A32D2D", label: "Cancelled" },
  no_show: { bg: "#F1EFE8", text: "#5F5E5A", label: "No-show" },
};

// Same palette and same index-based assignment as bookingCalendar.js — keeps colors in sync
const BARBER_COLORS = [
  { dot: "#378ADD" },
  { dot: "#EF9F27" },
  { dot: "#E24B4A" },
  { dot: "#7F77DD" },
  { dot: "#1D9E75" },
  { dot: "#D4537E" },
];

const cardSx = {
  borderRadius: 2,
  border: "0.1px solid",
  borderColor: "customDivider",
  backgroundColor: "offWhite",
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  height: "100%",
  boxSizing: "border-box",
};

// ── Period toggle ─────────────────────────────────────────────────────────────
const PeriodToggle = ({ value, onChange }) => (
  <Box sx={{ display: "flex", gap: 0.5 }}>
    {["Today", "This Week"].map((label) => (
      <Box
        key={label}
        onClick={() => onChange(label)}
        sx={{
          px: 1.5,
          py: 0.5,
          borderRadius: 1,
          fontSize: 13,
          fontWeight: 600,
          cursor: "pointer",
          border: "1px solid",
          borderColor: value === label ? "#1a1a1a" : "customDivider",
          backgroundColor: value === label ? "#1a1a1a" : "transparent",
          color: value === label ? "white" : "text.secondary",
          transition: "all 0.15s",
          userSelect: "none",
          "@media (hover: hover)": { "&:hover": {} },
        }}
      >
        {label}
      </Box>
    ))}
  </Box>
);

// ── Sort toggle ───────────────────────────────────────────────────────────────
const SortToggle = ({ value, onChange }) => (
  <Box sx={{ display: "flex", gap: 0.5 }}>
    {[
      { key: "count", label: "Appts" },
      { key: "revenue", label: "Revenue" },
    ].map(({ key, label }) => (
      <Box
        key={key}
        onClick={() => onChange(key)}
        sx={{
          px: 1.25,
          py: 0.25,
          borderRadius: 1,
          fontSize: 11,
          fontWeight: 600,
          cursor: "pointer",
          border: "1px solid",
          borderColor: value === key ? "#1a1a1a" : "customDivider",
          backgroundColor: value === key ? "#1a1a1a" : "transparent",
          color: value === key ? "white" : "text.secondary",
          transition: "all 0.15s",
          userSelect: "none",
        }}
      >
        {label}
      </Box>
    ))}
  </Box>
);

// ── Metric card ───────────────────────────────────────────────────────────────
const MetricCard = ({ icon, label, value, sub, detail }) => (
  <Box sx={{ ...cardSx, p: 2.5, display: "flex", flexDirection: "column" }}>
    {/* Top section — grows to push detail to bottom */}
    <Box sx={{ flex: 1 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 1,
            textTransform: "uppercase",
            color: GOLD,
          }}
        >
          {label}
        </Typography>
        <Box sx={{ color: GOLD, display: "flex" }}>{icon}</Box>
      </Box>
      <Typography
        variant="h3"
        sx={{ fontWeight: 700, mb: 0.25, letterSpacing: -0.5 }}
      >
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {sub}
        </Typography>
      )}
    </Box>

    {/* Detail — always anchored to the bottom of the card */}
    {detail && (
      <Box sx={{ mt: 1.5, pt: 1.5, borderTop: "1px solid #f0f0f0" }}>
        {detail}
      </Box>
    )}
  </Box>
);

// ── Main component ────────────────────────────────────────────────────────────
const Overview = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const api = useApi();
  const { user } = useAuth();
  const { tenant } = useTenant();
  const { setSelectedTab, setCalendarDate } = useDashContext();

  const [period, setPeriod] = useState("Today");
  const [todayBookings, setTodayBookings] = useState([]);
  const [weekBookings, setWeekBookings] = useState([]);
  const [barberColorMap, setBarberColorMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [schedulePage, setSchedulePage] = useState(0);
  const [barberSort, setBarberSort] = useState("count");
  const [serviceSort, setServiceSort] = useState("count");
  const ITEMS_PER_PAGE = 5;

  // Anchor "Today"/"This Week" to the shop's timezone, not the viewer's browser,
  // so the day/week windows match how the API buckets bookings by tenant time.
  const tzName = tenant?.timezone || "America/New_York";
  const nowTz = dayjs().tz(tzName);
  const today = nowTz.format("YYYY-MM-DD");
  const weekStart = nowTz.startOf("week").format("YYYY-MM-DD");
  const weekEnd = nowTz.endOf("week").format("YYYY-MM-DD");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setLoadError(false);
      // allSettled so one failing request doesn't blank the whole dashboard —
      // render whatever succeeded and surface a soft banner if anything failed.
      const [todayRes, weekRes, barbersRes] = await Promise.allSettled([
        api(`/tenants/appointments.php?date_from=${today}&date_to=${today}`, {
          method: "GET",
        }),
        api(
          `/tenants/appointments.php?date_from=${weekStart}&date_to=${weekEnd}`,
          { method: "GET" },
        ),
        api("/barbers/barbers.php", { method: "GET" }),
      ]);

      setSchedulePage(0);
      if (todayRes.status === "fulfilled") {
        setTodayBookings(todayRes.value.data.bookings || []);
      }
      if (weekRes.status === "fulfilled") {
        setWeekBookings(weekRes.value.data.bookings || []);
      }
      if (barbersRes.status === "fulfilled") {
        // Build the same color map as bookingCalendar.js — index-based assignment
        const map = {};
        (barbersRes.value.data.barbers || []).forEach((b, i) => {
          map[b.user_id] = BARBER_COLORS[i % BARBER_COLORS.length].dot;
        });
        setBarberColorMap(map);
      }

      if (
        [todayRes, weekRes, barbersRes].some((r) => r.status === "rejected")
      ) {
        setLoadError(true);
      }
      setLoading(false);
    };
    load();
    // Re-fetch if the tenant timezone resolves after first render (changes the dates).
  }, [today, weekStart, weekEnd]);

  useEffect(() => {
    setSchedulePage(0);
  }, [period]);

  const bookings = period === "Today" ? todayBookings : weekBookings;
  const periodLabel = period === "Today" ? "today" : "this week";

  // ── Metrics ─────────────────────────────────────────────────────────────────
  const totalToday = bookings.filter((b) => b.status !== "cancelled").length;

  // Collected — money that has actually been received and kept
  const collectedToday = bookings
    .filter(
      (b) =>
        Number(b.amount_paid) > 0 &&
        !(b.status === "cancelled" && b.payment_status === "refunded"),
    )
    .reduce((sum, b) => sum + Number(b.amount_paid || 0), 0);

  // Pending — upcoming cash appointments that will pay at the chair
  const pendingRevenue = bookings
    .filter(
      (b) =>
        b.payment_method === "cash" &&
        ["confirmed", "pending"].includes(b.status) &&
        b.payment_status === "unpaid",
    )
    .reduce((sum, b) => sum + Number(b.price_charged || 0), 0);
  const pendingRevenueCount = bookings.filter(
    (b) =>
      b.payment_method === "cash" &&
      ["confirmed", "pending"].includes(b.status) &&
      b.payment_status === "unpaid",
  ).length;

  // Lost — cancelled bookings where the money won't come in
  const lostRevenue = bookings
    .filter(
      (b) =>
        b.status === "cancelled" &&
        ["unpaid", "refunded"].includes(b.payment_status),
    )
    .reduce((sum, b) => sum + Number(b.price_charged || 0), 0);
  const cancelledCount = bookings.filter(
    (b) => b.status === "cancelled",
  ).length;
  const noShowCount = bookings.filter((b) => b.status === "no_show").length;
  const confirmedCount = bookings.filter(
    (b) => b.status === "confirmed",
  ).length;
  const pendingCount = bookings.filter((b) => b.status === "pending").length;
  const completedCount = bookings.filter(
    (b) => b.status === "completed",
  ).length;

  // ── Schedule sorted by start time + pagination ───────────────────────────────
  const todaySorted = [...bookings].sort((a, b) =>
    (a.starts_at_local || a.starts_at).localeCompare(
      b.starts_at_local || b.starts_at,
    ),
  );
  const totalPages = Math.ceil(todaySorted.length / ITEMS_PER_PAGE);
  const schedulePage_ = Math.min(schedulePage, Math.max(0, totalPages - 1));
  const pageBookings = todaySorted.slice(
    schedulePage_ * ITEMS_PER_PAGE,
    (schedulePage_ + 1) * ITEMS_PER_PAGE,
  );

  // ── By barber ────────────────────────────────────────────────────────────────
  const barberStatsMap = {};
  bookings
    .filter((b) => b.status !== "cancelled")
    .forEach((b) => {
      const key = b.barber?.user_id;
      if (!barberStatsMap[key]) {
        barberStatsMap[key] = {
          user_id: key,
          name: `${b.barber?.first_name ?? ""} ${b.barber?.last_name ?? ""}`.trim() || "Unknown",
          count: 0,
          revenue: 0,
          color:
            barberColorMap[key] ||
            BARBER_COLORS[
              Object.keys(barberStatsMap).length % BARBER_COLORS.length
            ].dot,
        };
      }
      barberStatsMap[key].count++;
      barberStatsMap[key].revenue += Number(b.price_charged || 0);
    });
  const barberStats = Object.values(barberStatsMap).sort((a, b) => {
    if (barberSort === "revenue") return b.revenue - a.revenue;
    // Sort by count; break ties by revenue descending
    return b.count !== a.count ? b.count - a.count : b.revenue - a.revenue;
  });
  const maxBarberCount = Math.max(...barberStats.map((b) => b.count), 1);
  const maxBarberRevenue = Math.max(...barberStats.map((b) => b.revenue), 1);

  // ── By service ───────────────────────────────────────────────────────────────
  const serviceStatsMap = {};
  bookings
    .filter((b) => b.status !== "cancelled")
    .forEach((b) => {
      const key = b.service?.name || "Unknown";
      if (!serviceStatsMap[key]) {
        serviceStatsMap[key] = { name: key, count: 0, revenue: 0 };
      }
      serviceStatsMap[key].count++;
      serviceStatsMap[key].revenue += Number(b.price_charged || 0);
    });
  const serviceStats = Object.values(serviceStatsMap).sort((a, b) =>
    serviceSort === "revenue" ? b.revenue - a.revenue : b.count - a.count,
  );
  const maxServiceCount = Math.max(...serviceStats.map((s) => s.count), 1);
  const maxServiceRevenue = Math.max(...serviceStats.map((s) => s.revenue), 1);

  // ── Week strip ───────────────────────────────────────────────────────────────
  const weekDays = Array.from({ length: 7 }, (_, i) =>
    nowTz.startOf("week").add(i, "day"),
  );
  const countByDay = {};
  weekBookings
    .filter((b) => b.status !== "cancelled")
    .forEach((b) => {
      const d = (b.starts_at_local || b.starts_at).slice(0, 10);
      countByDay[d] = (countByDay[d] || 0) + 1;
    });

  // ── Greeting ─────────────────────────────────────────────────────────────────
  const hour = nowTz.hour();
  const greeting =
    hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", px: { xs: 3, md: 5 }, py: 5 }}>
      {/* ── Header ──────────────────────────────────────────────── */}
      <Box
        sx={{
          mb: 4,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant={isMobile ? "h4" : "h3"}
            sx={{ fontWeight: 700, lineHeight: 1.2, mb: 0.25 }}
          >
            {greeting}
            {user?.first_name ? `, ${user.first_name}` : ""}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
            {tenant?.name
              ? `Here's how ${tenant.name} is doing ${periodLabel}.`
              : `Here's how your shop is doing ${periodLabel}.`}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "text.disabled", display: "block" }}
          >
            {nowTz.format("dddd, MMMM D, YYYY")}
          </Typography>
        </Box>
        <PeriodToggle value={period} onChange={setPeriod} />
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 6 }}>
          <CircularProgress size={18} sx={{ color: "text.secondary" }} />
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Loading your overview...
          </Typography>
        </Box>
      ) : (
        <>
          {loadError && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              Some of your data couldn't be loaded. Showing what we have —
              refresh to try again.
            </Alert>
          )}
          {/* ── Metric cards ────────────────────────────────────── */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
              gap: 2,
              mb: 4,
            }}
          >
            <MetricCard
              icon={<CalendarTodayIcon sx={{ fontSize: 18 }} />}
              label="Bookings"
              value={totalToday}
            />
            <MetricCard
              icon={<AttachMoneyIcon sx={{ fontSize: 18 }} />}
              label="Revenue"
              value={fmt(collectedToday)}
              sub="collected"
              detail={
                pendingRevenue > 0 ? (
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    <Box
                      component="span"
                      sx={{ fontWeight: 600, color: "text.primary" }}
                    >
                      +{fmt(pendingRevenue)}
                    </Box>{" "}
                    expected · {pendingRevenueCount} cash appt
                    {pendingRevenueCount !== 1 ? "s" : ""}
                  </Typography>
                ) : null
              }
            />
            <MetricCard
              icon={<EventBusyIcon sx={{ fontSize: 18 }} />}
              label="Cancelled"
              value={cancelledCount}
              detail={
                cancelledCount > 0 && lostRevenue > 0 ? (
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    <Box
                      component="span"
                      sx={{ fontWeight: 600, color: "#E24B4A" }}
                    >
                      -{fmt(lostRevenue)}
                    </Box>{" "}
                    missed from cancellations
                  </Typography>
                ) : null
              }
            />
            <MetricCard
              icon={<EventBusyIcon sx={{ fontSize: 18 }} />}
              label="No-Shows"
              value={noShowCount}
            />
          </Box>

          {/* ── Two-column: Schedule + By barber ────────────────── */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "3fr 2fr" },
              gap: 3,
              mb: 4,
              alignItems: "start",
            }}
          >
            {/* Today's Schedule */}
            <Box sx={{ ...cardSx, height: "auto", overflow: "hidden" }}>
              {/* Card header */}
              <Box
                sx={{
                  px: 3,
                  py: 2.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {period === "Today" ? "Today" : "This Week"}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  {totalPages > 1 && (
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Button
                        size="small"
                        disabled={schedulePage_ === 0}
                        onClick={() => setSchedulePage((p) => p - 1)}
                        sx={{ minWidth: 28, px: 0.5, color: "text.secondary" }}
                      >
                        ‹
                      </Button>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", mx: 0.5 }}
                      >
                        {schedulePage_ + 1} / {totalPages}
                      </Typography>
                      <Button
                        size="small"
                        disabled={schedulePage_ === totalPages - 1}
                        onClick={() => setSchedulePage((p) => p + 1)}
                        sx={{ minWidth: 28, px: 0.5, color: "text.secondary" }}
                      >
                        ›
                      </Button>
                    </Box>
                  )}
                  <Button
                    size="small"
                    onClick={() => setSelectedTab("Appointments")}
                    sx={{
                      textTransform: "none",
                      fontSize: 13,
                      color: GOLD,
                      fontWeight: 500,
                      p: 0,
                      minWidth: 0,
                      "&:hover": {
                        backgroundColor: "transparent",
                        opacity: 0.75,
                      },
                    }}
                  >
                    Calendar →
                  </Button>
                </Box>
              </Box>

              {/* Appointment rows */}
              {todaySorted.length === 0 ? (
                <Box sx={{ px: 3, py: 4 }}>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    No appointments scheduled {periodLabel}.
                  </Typography>
                </Box>
              ) : (
                pageBookings.map((b, i) => {
                  const status =
                    STATUS_STYLES[b.status] || STATUS_STYLES.confirmed;
                  const startLocal = b.starts_at_local || b.starts_at;
                  const endLocal = b.ends_at_local || b.ends_at;
                  const time = dayjs(startLocal).format(
                    period === "Today" ? "h:mm A" : "ddd h:mm A",
                  );
                  const durationMin = dayjs(endLocal).diff(
                    dayjs(startLocal),
                    "minute",
                  );
                  const dotColor =
                    barberColorMap[b.barber?.user_id] || BARBER_COLORS[0].dot;

                  return (
                    <Box key={b.id}>
                      {i > 0 && <Divider />}
                      <Box
                        sx={{
                          px: { xs: 2, md: 3 },
                          py: 2,
                          display: "flex",
                          alignItems: "center",
                          gap: { xs: 1.5, md: 2 },
                        }}
                      >
                        {/* Time + duration */}
                        <Box
                          sx={{
                            minWidth: period === "Today" ? 68 : 86,
                            flexShrink: 0,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, lineHeight: 1.3 }}
                          >
                            {time}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "text.secondary" }}
                          >
                            {durationMin} min
                          </Typography>
                        </Box>

                        {/* Color dot */}
                        <Box
                          sx={{
                            width: 9,
                            height: 9,
                            borderRadius: "50%",
                            backgroundColor: dotColor,
                            flexShrink: 0,
                          }}
                        />

                        {/* Client + service */}
                        <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600 }}
                            noWrap
                          >
                            {`${b.client?.first_name ?? ""} ${b.client?.last_name ?? ""}`.trim() || "Guest"}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "text.secondary", display: "block" }}
                            noWrap
                          >
                            {b.service?.name} · {b.barber?.first_name}{" "}
                            {b.barber?.last_name}
                          </Typography>
                        </Box>

                        {/* Status chip */}
                        <Chip
                          label={status.label}
                          size="small"
                          sx={{
                            backgroundColor: status.bg,
                            color: status.text,
                            fontWeight: 600,
                            fontSize: 11,
                            height: 22,
                            flexShrink: 0,
                          }}
                        />
                      </Box>
                    </Box>
                  );
                })
              )}
            </Box>

            {/* Right column — stacked cards */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Today by barber */}
              <Box sx={{ ...cardSx, height: "auto", overflow: "hidden" }}>
                <Box
                  sx={{
                    px: 3,
                    py: 2,
                    borderBottom: "1px solid #f0f0f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {period === "Today"
                      ? "Today by barber"
                      : "This week by barber"}
                  </Typography>
                  <SortToggle value={barberSort} onChange={setBarberSort} />
                </Box>
                <Box sx={{ px: 3, py: 2.5 }}>
                  {barberStats.length === 0 ? (
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      No appointments {periodLabel}.
                    </Typography>
                  ) : (
                    barberStats.map((barber, i) => (
                      <Box
                        key={barber.user_id}
                        sx={{ mb: i < barberStats.length - 1 ? 3 : 0 }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 0.75,
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {barber.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              fontWeight: 500,
                              flexShrink: 0,
                            }}
                          >
                            {barberSort === "revenue"
                              ? fmt(barber.revenue)
                              : `${barber.count} appt${barber.count !== 1 ? "s" : ""}`}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: "#f0efec",
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              height: "100%",
                              width: `${Math.round(((barberSort === "revenue" ? barber.revenue : barber.count) / (barberSort === "revenue" ? maxBarberRevenue : maxBarberCount)) * 100)}%`,
                              backgroundColor: barber.color,
                              borderRadius: 3,
                              transition: "width 0.6s ease",
                            }}
                          />
                        </Box>
                      </Box>
                    ))
                  )}
                </Box>
              </Box>

              {/* Today by service */}
              <Box sx={{ ...cardSx, height: "auto", overflow: "hidden" }}>
                <Box
                  sx={{
                    px: 3,
                    py: 2,
                    borderBottom: "1px solid #f0f0f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {period === "Today"
                      ? "Today by service"
                      : "This week by service"}
                  </Typography>
                  <SortToggle value={serviceSort} onChange={setServiceSort} />
                </Box>
                <Box sx={{ px: 3, py: 2.5 }}>
                  {serviceStats.length === 0 ? (
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      No appointments {periodLabel}.
                    </Typography>
                  ) : (
                    serviceStats.map((svc, i) => (
                      <Box
                        key={svc.name}
                        sx={{ mb: i < serviceStats.length - 1 ? 3 : 0 }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 0.75,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 500, mr: 1 }}
                            noWrap
                          >
                            {svc.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              fontWeight: 500,
                              flexShrink: 0,
                            }}
                          >
                            {serviceSort === "revenue"
                              ? fmt(svc.revenue)
                              : `${svc.count} appt${svc.count !== 1 ? "s" : ""}`}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: "#f0efec",
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              height: "100%",
                              width: `${Math.round(((serviceSort === "revenue" ? svc.revenue : svc.count) / (serviceSort === "revenue" ? maxServiceRevenue : maxServiceCount)) * 100)}%`,
                              backgroundColor: "#b0a898",
                              borderRadius: 3,
                              transition: "width 0.6s ease",
                            }}
                          />
                        </Box>
                      </Box>
                    ))
                  )}
                </Box>
              </Box>
            </Box>
          </Box>

          {/* ── This Week ───────────────────────────────────────── */}
          <Box sx={{ display: "none" }}>
            <Typography
              variant="overline"
              sx={{ color: "text.secondary", display: "block", mb: 2 }}
            >
              This Week
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: 1,
              }}
            >
              {weekDays.map((day) => {
                const dateStr = day.format("YYYY-MM-DD");
                const isToday = dateStr === today;
                const count = countByDay[dateStr] || 0;
                return (
                  <Box
                    key={dateStr}
                    onClick={() => {
                      setCalendarDate(day.toDate());
                      setSelectedTab("Appointments");
                    }}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: "0.1px solid",
                      textAlign: "center",
                      cursor: "pointer",
                      backgroundColor: isToday ? "#1a1a1a" : "offWhite",
                      borderColor: isToday ? "#1a1a1a" : "customDivider",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                      transition: "all 0.15s",
                      "@media (hover: hover)": {
                        "&:hover": {
                          borderColor: isToday ? "#000" : "#9b9b9b",
                        },
                      },
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: isToday
                          ? "rgba(255,255,255,0.6)"
                          : "text.secondary",
                        fontWeight: 500,
                        textTransform: "uppercase",
                        letterSpacing: 0.8,
                        fontSize: 10,
                      }}
                    >
                      {day.format("ddd")}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: isToday ? "white" : "text.primary",
                        my: 0.5,
                      }}
                    >
                      {day.format("D")}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: isToday
                          ? "rgba(255,255,255,0.6)"
                          : "text.secondary",
                        fontWeight: 500,
                      }}
                    >
                      {count > 0 ? `${count} appt${count > 1 ? "s" : ""}` : "—"}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default Overview;
